import { FC, useEffect, useState } from "react"
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins"
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel"
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { GoodsClassifierProvider } from "../../../Services/DataProviders/GoodsClassifierDataProvider";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { Spinner } from "../../../components/spiner/Spinner";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import { IGoodsClassifierViewDTO } from "../../../libs/coreapi-dto/dirs/goodsClassifier";
import { GoodsClassifierCreatorModal } from "./components/GoodsClassifierCreatorModal";
import { useDetailsTabsPanel } from "../../../system/hooks/useTabsPanel";
import tabsStyles from "../../../pages/styles/homePage.module.scss";
import { TabsPanel } from "../../../components/tabs";
import classNames from "classnames";
import { usePluginContext } from "../../../system/providers/plugin";
import { DocumentType } from "../../../@types/enumsGlobal";

export const GoodsClassifierPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const goodsClassifierDataProvider = new GoodsClassifierProvider(appContext.coreApiService);
    const pluginContext = usePluginContext();

    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [gridFilter, dispatchGridFilter] = useGridFilter(CreateSelectorGridFilter);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>();
    const [totalCount, setTotalCount] = useState(0);
    const [data, setData] = useState<IGoodsClassifierViewDTO[]>([]);
    const [detailsTabsPanel, dispatchDetailsTabsPanel] = useDetailsTabsPanel(props.plugin.mnemocode);

    useEffect(() => {
        setIsSubmitting(true);
        setSelectedItem(undefined);
        goodsClassifierDataProvider.getView(gridFilter, async (data, totalCount) => {
            setData(data);
            setTotalCount(totalCount);
            setLoadingStatus(LoadingStatus.Completed);
            setIsSubmitting(false);
        })
    }, [gridFilter])

    useEffect(() => {
        if (viewState === 'refresh') {
            setIsSubmitting(true);
            goodsClassifierDataProvider.getView(gridFilter, async (data, totalCount) => {
                setData(data);
                setViewState('view');
                setTotalCount(totalCount);
                setLoadingStatus(LoadingStatus.Completed);
                setIsSubmitting(false);
            })
        }
    }, [viewState])
    
    return (
        <PluginWrapper>
            <ToolbarWrapper>
                <DefaultDictionaryCommandsPanel
                    selectedItem={[selectedItem, setSelectedItem]}
                    gridFilter={[gridFilter, dispatchGridFilter]}
                    dataProvider={goodsClassifierDataProvider}
                    creatorModalJsx={GoodsClassifierCreatorModal}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    hiddenCopyButton={true} 
                />
            </ToolbarWrapper>
            {
                isSubmitting ? (
                    <Spinner />
                ) : (
                    <>
                        <DefaultGrid
                            showQuantity
                            gridId={props.gridId}
                            separator
                            data={data}
                            loadingStatus={loadingStatus}
                            setLoadingStatus={setLoadingStatus}
                            singleDirectory
                            totalCount={totalCount}
                            filter={gridFilter}
                            dataProvider={goodsClassifierDataProvider}
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            multipleSelect={props.multipleSelect}
                            onDoubleClick={(row) => props.onDoubleClick?.(row)}
                            onSelect={(row) => {
                                setSelectedItem(row);
                                row ? pluginContext.masterGrid.onSelectEvent(row as IGridRow, DocumentType.goodsClassifier) : pluginContext.masterGrid.onUnselectEvent();
                                props.onSelect?.(row);
                            }}
                            onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                            onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                            onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                            onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                            onEnter={() => setViewState("edit")}
                        />
                        {
                            <>
                                {selectedItem && (
                                    <>
                                        <div className={tabsStyles.tabsPanelWrapper}>
                                            <div id="detailsTabsPanelWrapper" style={{ position: "relative", overflow: "hidden", width: "100%" }}>
                                                <TabsPanel
                                                    id="detailsTabsPanel"
                                                    activeId={detailsTabsPanel.currentTab?.id}
                                                    tabs={detailsTabsPanel.tabs}
                                                    onActive={(id) => {
                                                        dispatchDetailsTabsPanel({ type: "activate", payload: id });
                                                    }}
                                                ></TabsPanel>
                                            </div>
                                        </div>
                                        <div className={tabsStyles.contentWrapper}>
                                            {detailsTabsPanel.tabs.map((item) => {
                                                return (
                                                    <div key={item.id} className={classNames(item.id === detailsTabsPanel.currentTab?.id ? tabsStyles.contentVisible : tabsStyles.contentHidden)}>
                                                        {item.view.content}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </>
                        }
                    </>
                )
            }
        </PluginWrapper>
    )
}